// App.tsx
import './App.css'
import React, { lazy, Suspense, useMemo, useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import type { ClassCategory, User } from './types'
import { subjectLink } from './pages/public/category/subjects'
import { AuthProvider, useAuth } from './contexts/authContext'
import { I18nProvider } from './i18n/I18nProvider'
import { useI18n } from './i18n/I18nContext'
import { LanguageCode } from './types'
import { ThemeProvider } from './contexts/ThemeContext'
import { PublicOnlyRoute, ProtectedRoute } from './components/layout/ProtectedRoute'
import PublicRouteLoading from './components/ui/PublicRouteLoading'
import ExpiryReengagementModal from './components/subscription/ExpiryReengagementModal'
import { ErrorBoundary, InlineErrorFallback } from './components/ui/ErrorBoundary'
import { AnnouncementBanner } from './components/AnnouncementBanner'
import { NetworkStatusProvider } from './providers/NetworkStatusProvider'
import NetworkRecoveryBoundary from './components/layout/NetworkRecoveryBoundary'


// OAuth2Callback is intentionally NOT lazy-loaded.
// It runs in a popup window, reads a token from the URL, and fires postMessage
// to the parent window immediately on mount. Any Suspense delay can corrupt the
// search-params read under certain React/router timing conditions.
// At 0.63 KB it has zero impact on the main bundle.
import OAuth2Callback from './pages/auth/OAuth2Callback'

// Public pages have separate chunks. The build preloads the matching page on a
// direct visit, keeping the landing's painting code out of catalog startup.
const LandingPage = lazy(() => import('./pages/public/LandingPage'))

// The auth pages are lazy, so they stay out of the landing's critical chunk, and
// are fetched as soon as the first page is idle, so "Log in" and "Sign up" still
// open instantly.
const loadLogin  = () => import('./pages/auth/LoginPage')
const loadSignup = () => import('./pages/auth/SignupPage')
const LoginPage          = lazy(loadLogin)
const SignupPage         = lazy(loadSignup)
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'))
const ResetPasswordPage  = lazy(() => import('./pages/auth/ResetPasswordPage'))
function prefetchAuthPages() {
    const run = () => { loadLogin().catch(() => undefined); loadSignup().catch(() => undefined) }
    // A cold catalog visit must finish its discovery request and covers first.
    // Login still loads immediately when clicked; this only postpones warming it.
    if (window.location.pathname.replace(/\/$/, '') === '/classes') {
        const timer = setTimeout(run, 10000)
        return () => clearTimeout(timer)
    }
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 5000 })
    else setTimeout(run, 2000)
}

// ── Lazy-loaded route chunks ──────────────────────────────────────────────────
// Each lazy() call becomes a separate Vite chunk, loaded only when that route
// is first visited. Heavy pages (dashboard, admin, lesson player) stay lazy.

// Legal pages
// DEV-ONLY atom gallery (design work). In prod this is a no-op so the chunk is
// tree-shaken away and the route below never renders.
const AtomPreview = import.meta.env.DEV ? lazy(() => import('./pages/dev/AtomPreview')) : () => null
// DEV-ONLY feedback lab — every Lesson Shell feedback state on one page, each replayable.
const FeedbackLab = import.meta.env.DEV ? lazy(() => import('./pages/dev/FeedbackLab')) : () => null
// DEV-ONLY glossary lab — three ways to show the meaning of a word, to be chosen by looking.
const GlossaryLab = import.meta.env.DEV ? lazy(() => import('./pages/dev/GlossaryLab')) : () => null
const DevLessonLab = import.meta.env.DEV ? lazy(() => import('./components/web-editor/DevLessonLab')) : () => null
const PrivacyPolicyPage   = lazy(() => import('./pages/legal/PrivacyPolicyPage'))
const TermsOfServicePage  = lazy(() => import('./pages/legal/TermsOfServicePage'))
const RefundPolicyPage    = lazy(() => import('./pages/legal/RefundPolicyPage'))
const CookiePolicyPage    = lazy(() => import('./pages/legal/CookiePolicyPage'))

// Public pages
const AboutPage         = lazy(() => import('./pages/public/AboutPage'))
const ClassesPage       = lazy(() => import('./pages/public/ClassesPage'))
const ClassPage         = lazy(() => import('./pages/public/ClassPage'))
const PricingPage       = lazy(() => import('./pages/public/PricingPage'))
const CheckoutPage      = lazy(() => import('./pages/payment/CheckoutPage'))
const PaymentCallbackPage = lazy(() => import('./pages/public/PaymentCallbackPage'))
const AppHandoffFallbackPage = lazy(() => import('./pages/public/AppHandoffFallbackPage'))
const UnsubscribePage         = lazy(() => import('./pages/public/UnsubscribePage'))
const CertificateVerifyPage   = lazy(() => import('./pages/public/CertificateVerifyPage'))
const DeleteAccountPage  = lazy(() => import('./pages/public/DeleteAccountPage'))

// Error pages — lazy (never hit on the happy path)
const NotFoundPage    = lazy(() => import('./pages/error/NotFoundPage'))

// Auth pages (secondary — lazy is fine)
const VerifyEmailPage   = lazy(() => import('./pages/auth/VerifyEmailPage'))

// Student pages
const Dashboard         = lazy(() => import('./pages/student/Dashboard'))
const Settings          = lazy(() => import('./pages/student/Settings'))
const LessonPlayer      = lazy(() => import('./pages/student/LessonPlayer'))
const CourseCompletePage    = lazy(() => import('./pages/student/CourseCompletePage'))
const StageCompletePage     = lazy(() => import('./pages/student/StageCompletePage'))
const LessonComingSoonPage  = lazy(() => import('./pages/student/LessonComingSoonPage'))
const AchievementsPage   = lazy(() => import('./pages/student/AchievementsPage'))
const AnalyticsPage      = lazy(() => import('./pages/student/AnalyticsPage'))
const ClassCompletePage  = lazy(() => import('./pages/student/ClassCompletePage'))
const CertificatePage    = lazy(() => import('./pages/student/CertificatePage'))

// Admin pages
const AdminDashboard    = lazy(() => import('./pages/admin/AdminDashboard'))
const AdminClassAnalytics = lazy(() => import('./components/admin/AdminClassAnalytics'))
const AdminStudentDetail = lazy(() => import('./components/admin/AdminStudentDetail'))
const BannerManagement  = lazy(() => import('./pages/admin/BannerManagement'))
const AdminWhitelist    = lazy(() => import('./pages/admin/AdminWhitelist'))
const AdminEditLayer    = lazy(() => import('./components/web-editor/AdminEditLayer'))
const ReviewerNotesPage = lazy(() => import('./components/web-editor/ReviewerNotesPage'))
const AdminClassPage = lazy(() => import('./pages/admin/AdminClassPage'))

// Redirect the legacy enrolled-only class URL → the one class page. Kept (rather than
// deleted) because it is linked from old emails, the mobile app and any bookmark a student
// already has; every /class/:classId/... classroom route still resolves normally.
const LegacyClassRedirect: React.FC = () => {
    const { classId } = useParams<{ classId: string }>()
    return <Navigate to={`/classes/${classId}`} replace />
}

// Old subject-page links (short and long) and the catalog category each now opens.
const SUBJECT_REDIRECTS: [string, ClassCategory][] = [
    ['/cs', 'TECHNOLOGY'], ['/technology', 'TECHNOLOGY'],
    ['/is', 'ISLAMIC_SCIENCES'], ['/islamic', 'ISLAMIC_SCIENCES'],
    ['/lang', 'LANGUAGE'], ['/languages', 'LANGUAGE'],
]

const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation()
    useEffect(() => { window.scrollTo(0, 0) }, [pathname])
    return null
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN APP CONTENT
// ══════════════════════════════════════════════════════════════════════════════

const EXPIRY_MODAL_KEY = 'dhtn-expiry-modal-shown'

const AppContent: React.FC = () => {
    const { pathname } = useLocation()
    const { user: authUser, logout } = useAuth()
    const { language, setLanguage } = useI18n()
    const [showExpiryModal, setShowExpiryModal] = useState(false)

    useEffect(prefetchAuthPages, [])

    useEffect(() => {
        if (!authUser?.preferredLanguage) return
        // Guard: if the user has an explicit browser language preference, never override it.
        // Without this, returning from Paystack (which calls checkAuth) overwrites the user's
        // current language with the backend default ('ha'), causing the language flicker bug.
        const localLang = localStorage.getItem('dhtn-lang')
        if (localLang) return
        if (authUser.preferredLanguage !== language) {
            setLanguage(authUser.preferredLanguage as LanguageCode)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUser?.preferredLanguage])

    useEffect(() => {
        if (!authUser) return
        const { hasActiveSubscription, subscriptionExpiresAt } = authUser
        if (hasActiveSubscription || !subscriptionExpiresAt) return
        const expiresAt = new Date(subscriptionExpiresAt)
        if (expiresAt > new Date()) return
        if (localStorage.getItem(EXPIRY_MODAL_KEY) === subscriptionExpiresAt) return
        localStorage.setItem(EXPIRY_MODAL_KEY, subscriptionExpiresAt)
        setShowExpiryModal(true)
    }, [authUser])

    // Map AuthUser (from backend) to frontend User type
    // Memoized to prevent recreation on every render
    const frontendUser: User | null = useMemo(() => {
        if (!authUser) return null

        const isPremium =
            authUser.hasActiveSubscription === true &&
            (!authUser.subscriptionExpiresAt ||
                new Date(authUser.subscriptionExpiresAt).getTime() > Date.now())

        return {
            id: authUser.id?.toString() || '',
            name:
                authUser.name ||
                `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim() ||
                authUser.email,
            email: authUser.email,
            role: (authUser.role?.toLowerCase() === 'admin' ? 'admin' : 'student') as
                | 'student'
                | 'admin',
            firstName: authUser.firstName || '',
            lastName: authUser.lastName || '',
            hasActiveSubscription: authUser.hasActiveSubscription,
            reviewClassIds: authUser.reviewClassIds ?? [],
            subscriptionExpiresAt: authUser.subscriptionExpiresAt || undefined,
            subscriptionTier: authUser.subscriptionTier,
            isPremium,
            picture: authUser.picture,
            avatar: authUser.picture,
            provider: authUser.provider,
            enabled: authUser.enabled,
            createdAt: authUser.createdAt,
            updatedAt: authUser.updatedAt,
            hasPassword: authUser.hasPassword,
            preferredLanguage: authUser.preferredLanguage,
        }
    }, [authUser])


    return (
        <NetworkRecoveryBoundary>
            <AnnouncementBanner />
            {showExpiryModal && (
                <ExpiryReengagementModal onClose={() => setShowExpiryModal(false)} />
            )}
            <Suspense fallback={<PublicRouteLoading pathname={pathname} />}>
            <Routes>
                {/* ══════════════════════════════════════════════════════════════ */}
                {/* PUBLIC ROUTES (accessible to everyone) */}
                {/* ══════════════════════════════════════════════════════════════ */}

                {import.meta.env.DEV && <Route path="/dev/atoms" element={<AtomPreview />} />}
                {import.meta.env.DEV && <Route path="/dev/feedback" element={<FeedbackLab />} />}
                {import.meta.env.DEV && <Route path="/dev/glossary" element={<GlossaryLab />} />}
                {import.meta.env.DEV && <Route path="/dev/lesson-lab" element={<DevLessonLab />} />}
                <Route path="/about" element={<AboutPage />} />
                {/* The subject pages were removed (founder, 13 Oct 2026); their old links open the catalog row. */}
                {SUBJECT_REDIRECTS.map(([path, category]) => (
                    <Route key={path} path={path} element={<Navigate to={subjectLink(category)} replace />} />
                ))}
                {/* /join was retired; old shared links land on the home page. */}
                <Route path="/join" element={<Navigate to="/" replace />} />
                <Route path="/oauth-callback" element={<OAuth2Callback />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route
                    path="/checkout"
                    element={
                        <ProtectedRoute>
                            <CheckoutPage />
                        </ProtectedRoute>
                    }
                />
                <Route path="/payment/callback" element={<PaymentCallbackPage />} />
                <Route path="/app/handoff" element={<AppHandoffFallbackPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/verify/:certNumber" element={<CertificateVerifyPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/delete-account" element={<DeleteAccountPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/refund" element={<RefundPolicyPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />

                {/* Public class catalog - accessible without auth */}
                <Route path="/classes" element={<ClassesPage user={frontendUser} />} />
                <Route path="/classes/:id" element={<ClassPage user={frontendUser} />} />

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* PUBLIC-ONLY ROUTES (login/signup - redirect if logged in) */}
                {/* ══════════════════════════════════════════════════════════════ */}

                <Route
                    path="/login"
                    element={
                        <PublicOnlyRoute>
                            <LoginPage />
                        </PublicOnlyRoute>
                    }
                />
                <Route
                    path="/signup"
                    element={
                        <PublicOnlyRoute>
                            <SignupPage />
                        </PublicOnlyRoute>
                    }
                />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                    path="/forgot-password"
                    element={
                        <PublicOnlyRoute>
                            <ForgotPasswordPage />
                        </PublicOnlyRoute>
                    }
                />
                <Route
                    path="/reset-password"
                    element={
                        <PublicOnlyRoute>
                            <ResetPasswordPage />
                        </PublicOnlyRoute>
                    }
                />

                {/* Landing page - redirect to dashboard if logged in */}
                <Route
                    path="/"
                    element={
                        <PublicOnlyRoute>
                            <LandingPage />
                        </PublicOnlyRoute>
                    }
                />

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* STUDENT ROUTES (protected - require auth) */}
                {/* ══════════════════════════════════════════════════════════════ */}

                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            {frontendUser && <Dashboard user={frontendUser} onLogout={logout} />}
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute>
                            {frontendUser && <Settings user={frontendUser} onLogout={logout} />}
                        </ProtectedRoute>
                    }
                />

                {/* There is ONE class page. This legacy URL used to be a second, enrolled-only
                    copy of it; it now redirects to the canonical public one, which adapts to auth.
                    The /class/:classId/... routes below are unaffected — those are in-classroom
                    screens, not the class page. */}
                <Route path="/class/:classId" element={<LegacyClassRedirect />} />

                {/* Lesson player — actual lesson content */}
                <Route
                    path="/class/:classId/lesson/:lessonId"
                    element={
                        <ProtectedRoute>
                            <ErrorBoundary fallback={<InlineErrorFallback onReset={() => window.location.reload()} />}>
                                <LessonPlayer />
                            </ErrorBoundary>
                        </ProtectedRoute>
                    }
                />

                {/* Course complete — full-page celebration after finishing a course */}
                <Route
                    path="/class/:classId/course-complete"
                    element={
                        <ProtectedRoute>
                            <CourseCompletePage />
                        </ProtectedRoute>
                    }
                />

                {/* Stage complete — full-page celebration after finishing every course in a stage */}
                <Route
                    path="/class/:classId/stage-complete"
                    element={
                        <ProtectedRoute>
                            <StageCompletePage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/class/:classId/lesson-coming-soon"
                    element={
                        <ProtectedRoute>
                            <LessonComingSoonPage user={frontendUser ?? undefined} />
                        </ProtectedRoute>
                    }
                />

                {/* Certificates — earned credential wall + progress toward the next (route kept as /achievements) */}
                <Route
                    path="/achievements"
                    element={
                        <ProtectedRoute>
                            {frontendUser && <AchievementsPage user={frontendUser} />}
                        </ProtectedRoute>
                    }
                />

                {/* Analytics — dedicated premium learning history page */}
                <Route
                    path="/analytics"
                    element={
                        <ProtectedRoute>
                            {frontendUser && <AnalyticsPage user={frontendUser} />}
                        </ProtectedRoute>
                    }
                />

                {/* Class complete — full-screen ceremony after finishing all stages */}
                <Route
                    path="/class/:classId/class-complete"
                    element={
                        <ProtectedRoute>
                            <ClassCompletePage />
                        </ProtectedRoute>
                    }
                />

                {/* Certificate — dedicated full-screen view + download */}
                <Route
                    path="/certificate/:certId"
                    element={
                        <ProtectedRoute>
                            <CertificatePage user={frontendUser ?? undefined} />
                        </ProtectedRoute>
                    }
                />

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* ADMIN ROUTES (protected — admin only) */}
                {/* ══════════════════════════════════════════════════════════════ */}

                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute>
                            {frontendUser?.role === 'admin' ? (
                                <><AdminDashboard /><AdminEditLayer /></>
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )}
                        </ProtectedRoute>
                    }
                />

                {/* Class analytics */}
                <Route
                    path="/admin/classes/:classId/analytics"
                    element={
                        <ProtectedRoute>
                            {frontendUser?.role === 'admin' ? (
                                <AdminClassAnalytics />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )}
                        </ProtectedRoute>
                    }
                />

                {/* Class-first content editor, isolated from learner routes */}
                <Route
                    path="/admin/classes/:id"
                    element={<ProtectedRoute>{frontendUser?.role === 'admin' ? <><AdminClassPage /><AdminEditLayer /></> : <Navigate to="/dashboard" replace />}</ProtectedRoute>}
                />

                {/* Student detail */}
                <Route
                    path="/admin/students/:id"
                    element={
                        <ProtectedRoute>
                            {frontendUser?.role === 'admin' ? (
                                <AdminStudentDetail />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )}
                        </ProtectedRoute>
                    }
                />


                <Route
                    path="/admin/banner"
                    element={
                        <ProtectedRoute>
                            {frontendUser?.role === 'admin' ? (
                                <BannerManagement />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )}
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/whitelist"
                    element={
                        <ProtectedRoute>
                            {frontendUser?.role === 'admin' ? (
                                <AdminWhitelist />
                            ) : (
                                <Navigate to="/dashboard" replace />
                            )}
                        </ProtectedRoute>
                    }
                />

                {/* ══════════════════════════════════════════════════════════════ */}
                {/* FALLBACK */}
                {/* ══════════════════════════════════════════════════════════════ */}

                <Route path="*" element={<NotFoundPage />} />
                <Route path="/admin/reviewer-notes" element={<ProtectedRoute>{frontendUser?.role === 'admin' ? <ReviewerNotesPage /> : <Navigate to="/dashboard" replace />}</ProtectedRoute>} />
            </Routes>
        </Suspense>
        </NetworkRecoveryBoundary>
    )
}

// ══════════════════════════════════════════════════════════════════════════════
// APP ROOT
// ══════════════════════════════════════════════════════════════════════════════

const App: React.FC = () => {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <ThemeProvider>
                    <I18nProvider>
                        <NetworkStatusProvider>
                            <Router>
                                <ScrollToTop />
                                <AppContent />
                            </Router>
                        </NetworkStatusProvider>
                    </I18nProvider>
                </ThemeProvider>
            </AuthProvider>
        </ErrorBoundary>
    )
}

export default App
